import { supabase } from '../lib/supabase';
import { propostaMetaService } from './propostaMetaService';
import { fixEncodingIssues } from '../utils/textUtils';

export type CategoriaItemProposta = 'veiculo' | 'ajudante' | 'servico' | 'outro';

export type NivelAlertaSaldo =
  | 'normal'        // Saldo saudável
  | 'baixo_10'      // Saldo baixo (10 unidades ou adaptado para pequenos contratos)
  | 'critico_5'     // Alerta crítico (5 unidades ou adaptado para pequenos contratos)
  | 'urgente_1'     // Última unidade disponível (saldo = 1)
  | 'esgotado_0'    // Esgotado / Bloqueio (saldo <= 0)
  | 'sem_cota';     // Sem cota pré-fixada

export interface PropostaItemSaldo {
  id: string;
  servicos: string;
  categoria: CategoriaItemProposta;
  quantidadeContratada: number;
  quantidadeUtilizada: number;
  saldoRestante: number;
  temSaldo: boolean;
  percentualUtilizado: number;
  statusSaldo: 'disponivel' | 'alerta' | 'esgotado' | 'sem_cota';
  nivelAlerta: NivelAlertaSaldo;
  alertaTitulo: string;
  alertaMensagem: string;
  alertaBloqueado: boolean;
  valor_unitario: number;
  valor_total: number;
}

export interface PropostaSaldoInfo {
  pedidoId: number;
  clienteNome?: string;
  clienteId?: number;
  totalContratado: number;      // Soma total de todos os itens com cota
  totalUtilizado: number;       // Total utilizado
  saldoRestante: number;        // totalContratado - totalUtilizado
  temSaldo: boolean;            // Pelo menos um item tem saldo ou saldoRestante > 0
  percentualUtilizado: number;  // 0 a 100%
  statusSaldo: 'disponivel' | 'alerta' | 'esgotado' | 'sem_cota';
  nivelAlertaGeral: NivelAlertaSaldo;
  alertaTituloGeral: string;
  alertaMensagemGeral: string;
  alertaBloqueadoGeral: boolean;
  
  // Itens decompostos por categoria e individualmente
  itens: PropostaItemSaldo[];
  itensVeiculo: PropostaItemSaldo[];
  itensAjudante: PropostaItemSaldo[];
  itensOutros: PropostaItemSaldo[];
}

const PREF_KEY_BLOQUEIO_SALDO = 'config_bloquear_os_sem_saldo';
const LOCAL_STORAGE_KEY_BLOQUEIO = 'voope_bloquear_os_sem_saldo';

/**
 * Categoriza o tipo de item baseado na descrição/serviço
 */
export function categorizarItemProposta(nomeServico: string): CategoriaItemProposta {
  const s = (nomeServico || '').toLowerCase();
  
  if (
    s.includes('ajudante') ||
    s.includes('auxiliar') ||
    s.includes('chapa') ||
    s.includes('carregador') ||
    s.includes('ajud.') ||
    s.includes('ajud ')
  ) {
    return 'ajudante';
  }

  if (
    s.includes('truck') ||
    s.includes('toco') ||
    s.includes('munck') ||
    s.includes('hr') ||
    s.includes('carreta') ||
    s.includes('cavalo') ||
    s.includes('van') ||
    s.includes('furgão') ||
    s.includes('furgao') ||
    s.includes('veiculo') ||
    s.includes('veículo') ||
    s.includes('caminhao') ||
    s.includes('caminhão') ||
    s.includes('3/4') ||
    s.includes('vuc') ||
    s.includes('bitrem') ||
    s.includes('rodotrem') ||
    s.includes('grade baixa') ||
    s.includes('baú') ||
    s.includes('bau') ||
    s.includes('sider') ||
    s.includes('prancha') ||
    s.includes('utilitário') ||
    s.includes('utilitario') ||
    s.includes('passeio') ||
    s.includes('moto')
  ) {
    return 'veiculo';
  }

  return 'servico';
}

/**
 * Avalia o nível de alerta do saldo com regras inteligentes:
 * - 0 unidades: Bloqueio (saldo esgotado)
 * - 1 unidade: Aviso urgente de última unidade
 * - 5 unidades: Alerta crítico (em contratos > 15) ou <= 2 unidades em contratos pequenos (ex: 10 fretes)
 * - 10 unidades: Aviso de saldo baixo (em contratos > 15) ou <= 3 unidades (30%) em contratos pequenos
 */
export function avaliarNivelAlertaSaldo(contratado: number, saldoRestante: number): {
  nivel: NivelAlertaSaldo;
  titulo: string;
  mensagem: string;
  bloqueado: boolean;
  statusLegado: 'disponivel' | 'alerta' | 'esgotado' | 'sem_cota';
} {
  if (contratado <= 0) {
    return {
      nivel: 'sem_cota',
      titulo: 'Sem Cota Fixa',
      mensagem: 'Contrato sob demanda sem limite de cota pré-fixado.',
      bloqueado: false,
      statusLegado: 'sem_cota'
    };
  }

  // 1. Ao chegar em zero: Bloqueio
  if (saldoRestante <= 0) {
    return {
      nivel: 'esgotado_0',
      titulo: 'Bloqueio: Saldo Esgotado',
      mensagem: 'Cota de serviços 100% consumida. Emissão bloqueada para renovação ou aditivo contratual.',
      bloqueado: true,
      statusLegado: 'esgotado'
    };
  }

  // 2. Ao chegar em 1 unidade: Aviso urgente
  if (saldoRestante === 1) {
    return {
      nivel: 'urgente_1',
      titulo: 'Aviso: Última Unidade Disponível',
      mensagem: 'Atenção! Resta apenas 1 unidade disponível nesta proposta. Providencie a renovação.',
      bloqueado: false,
      statusLegado: 'alerta'
    };
  }

  // 3. Ao chegar em 5 unidades: Alerta crítico
  // Para propostas menores (ex: 10 fretes), 5 unidades é 50% do contrato (não faz sentido disparar alerta crítico na metade).
  // Portanto, para contratos com <= 15 unidades, o alerta crítico dispara em <= 2 unidades (20% da cota).
  const ehContratoPequeno = contratado <= 15;
  const limiteCritico = ehContratoPequeno ? Math.max(2, Math.floor(contratado * 0.2)) : 5;
  if (saldoRestante <= limiteCritico) {
    return {
      nivel: 'critico_5',
      titulo: 'Alerta Crítico: Cota Quase Esgotada',
      mensagem: `Alerta Crítico: restam apenas ${saldoRestante} de ${contratado} unidades contratadas (${Math.round((saldoRestante / contratado) * 100)}% restante).`,
      bloqueado: false,
      statusLegado: 'alerta'
    };
  }

  // 4. Ao chegar em 10 unidades: Aviso de saldo baixo
  // Para propostas com apenas 10 fretes, saldo = 10 é novo/intacto (100%), logo não deve avisar.
  // Para contratos com <= 15 unidades, dispara quando o saldo estiver em <= 3 unidades (30%) e já tiver havido consumo.
  const limiteBaixo = ehContratoPequeno ? Math.max(2, Math.floor(contratado * 0.35)) : 10;
  if (saldoRestante <= limiteBaixo && saldoRestante < contratado) {
    return {
      nivel: 'baixo_10',
      titulo: 'Aviso: Saldo Baixo',
      mensagem: `Aviso: restam ${saldoRestante} unidades de ${contratado} disponíveis na sua proposta comercial.`,
      bloqueado: false,
      statusLegado: 'alerta'
    };
  }

  return {
    nivel: 'normal',
    titulo: 'Saldo Regular',
    mensagem: `${saldoRestante} unidades disponíveis de ${contratado} contratadas.`,
    bloqueado: false,
    statusLegado: 'disponivel'
  };
}

/**
 * Calcula os atributos de saldo para um item
 */
function calcularStatusItem(contratado: number, utilizado: number): {
  saldoRestante: number;
  temSaldo: boolean;
  percentualUtilizado: number;
  statusSaldo: 'disponivel' | 'alerta' | 'esgotado' | 'sem_cota';
  nivelAlerta: NivelAlertaSaldo;
  alertaTitulo: string;
  alertaMensagem: string;
  alertaBloqueado: boolean;
} {
  const saldoRestante = contratado - utilizado;
  const temSaldo = contratado > 0 ? saldoRestante > 0 : true;

  let percentualUtilizado = 0;
  if (contratado > 0) {
    percentualUtilizado = Math.min(100, Math.round((utilizado / contratado) * 100));
  }

  const avaliacao = avaliarNivelAlertaSaldo(contratado, saldoRestante);

  return {
    saldoRestante,
    temSaldo,
    percentualUtilizado,
    statusSaldo: avaliacao.statusLegado,
    nivelAlerta: avaliacao.nivel,
    alertaTitulo: avaliacao.titulo,
    alertaMensagem: avaliacao.mensagem,
    alertaBloqueado: avaliacao.bloqueado
  };
}

export const propostaSaldoService = {
  /**
   * Obtém a configuração global se criação de OS sem saldo deve ser bloqueada.
   * Padrão: false (permite criar, exibindo apenas aviso em destaque).
   */
  async isBloqueioSemSaldoAtivo(): Promise<boolean> {
    try {
      const { data, error } = await supabase
        .from('system_preference')
        .select('preference')
        .eq('id', PREF_KEY_BLOQUEIO_SALDO)
        .maybeSingle();

      if (!error && data?.preference) {
        const val = data.preference === 'true' || data.preference === true || data.preference === '1';
        localStorage.setItem(LOCAL_STORAGE_KEY_BLOQUEIO, val ? 'true' : 'false');
        return val;
      }

      const cached = localStorage.getItem(LOCAL_STORAGE_KEY_BLOQUEIO);
      if (cached !== null) {
        return cached === 'true';
      }

      return false;
    } catch (e) {
      console.warn('[propostaSaldoService] Erro ao ler configuração de bloqueio de saldo:', e);
      return localStorage.getItem(LOCAL_STORAGE_KEY_BLOQUEIO) === 'true';
    }
  },

  /**
   * Salva a configuração de bloqueio de OS sem saldo
   */
  async setBloqueioSemSaldo(ativo: boolean): Promise<boolean> {
    try {
      localStorage.setItem(LOCAL_STORAGE_KEY_BLOQUEIO, ativo ? 'true' : 'false');

      await supabase
        .from('system_preference')
        .upsert({
          id: PREF_KEY_BLOQUEIO_SALDO,
          preference: ativo ? 'true' : 'false'
        });

      window.dispatchEvent(new CustomEvent('config_bloqueio_saldo_changed', { detail: { ativo } }));
      return true;
    } catch (e) {
      console.error('[propostaSaldoService] Erro ao salvar configuração de bloqueio:', e);
      return false;
    }
  },

  async setBloqueioSemSaldoAtivo(ativo: boolean): Promise<boolean> {
    return this.setBloqueioSemSaldo(ativo);
  },

  /**
   * Calcula detalhadamente o saldo de cada item de uma proposta específica
   * @param pedidoId ID da proposta/pedido
   * @param currentOsId ID da OS que está sendo editada (se houver), para não contar duplamente
   */
  async getSaldoProposta(pedidoId: number, currentOsId?: number): Promise<PropostaSaldoInfo> {
    try {
      // 1. Buscar os detalhes da proposta (itens e cliente)
      const { data: pedido, error: pedErr } = await supabase
        .from('pedido')
        .select('id, pessoa_id, pessoa:pessoa_id(nome), detalhes_do_pedido(*)')
        .eq('id', pedidoId)
        .single();

      if (pedErr || !pedido) {
        return {
          pedidoId,
          totalContratado: 0,
          totalUtilizado: 0,
          saldoRestante: 0,
          temSaldo: false,
          percentualUtilizado: 0,
          statusSaldo: 'sem_cota',
          nivelAlertaGeral: 'sem_cota',
          alertaTituloGeral: '',
          alertaMensagemGeral: '',
          alertaBloqueadoGeral: false,
          itens: [],
          itensVeiculo: [],
          itensAjudante: [],
          itensOutros: []
        };
      }

      const rawDetails = pedido.detalhes_do_pedido || [];
      const { items: cleanItems } = propostaMetaService.separateItemsAndMeta(rawDetails);

      // 2. Buscar Ordens de Serviço emitidas para esta proposta (exclui canceladas status_id = 3)
      let query = supabase
        .from('ordem_de_servico')
        // `servico` não existe em ordem_de_servico e derrubava a consulta (42703).
        .select('id, item_servico, item_ajudante, quantidade_ajudante')
        .eq('pedido_id', pedidoId)
        .neq('status_id', 3); // 3 = Cancelado

      if (currentOsId) {
        query = query.neq('id', currentOsId);
      }

      const { data: osList, error: osErr } = await query;
      const oss = osList || [];

      // Mapeamento de contagem por ID de item de serviço e item de ajudante
      const itemServicoUsage = new Map<string, number>();
      const itemAjudanteUsage = new Map<string, number>();

      oss.forEach((os: any) => {
        if (os.item_servico) {
          const key = String(os.item_servico);
          itemServicoUsage.set(key, (itemServicoUsage.get(key) || 0) + 1);
        }
        if (os.item_ajudante) {
          const key = String(os.item_ajudante);
          const qtd = os.quantidade_ajudante && Number(os.quantidade_ajudante) > 0 ? Number(os.quantidade_ajudante) : 1;
          itemAjudanteUsage.set(key, (itemAjudanteUsage.get(key) || 0) + qtd);
        }
      });

      // 3. Montar cada PropostaItemSaldo individualmente
      const parsedItems: PropostaItemSaldo[] = cleanItems.map((item: any) => {
        const itemId = String(item.id || '');
        const nomeServico = fixEncodingIssues(item.servicos || '');
        const categoria = categorizarItemProposta(nomeServico);
        const quantidadeContratada = Number(item.quantidade) || 0;
        const valorUnitario = Number(item.valor_unitario) || 0;
        const valorTotal = Number(item.valor_total) || (quantidadeContratada * valorUnitario);

        let quantidadeUtilizada = 0;
        if (categoria === 'ajudante') {
          // Uso de ajudante contabiliza por item_ajudante ou fallback
          quantidadeUtilizada = itemAjudanteUsage.get(itemId) || 0;
        } else {
          // Uso de serviço/veículo contabiliza por item_servico
          quantidadeUtilizada = itemServicoUsage.get(itemId) || 0;
        }

        // Caso legado: se a OS não salvou item_servico mas salvou texto idêntico no campo servico
        if (quantidadeUtilizada === 0 && nomeServico) {
          const matchesByText = oss.filter(
            (os: any) => !os.item_servico && os.servico && os.servico.trim().toLowerCase() === nomeServico.trim().toLowerCase()
          );
          if (matchesByText.length > 0) {
            quantidadeUtilizada += matchesByText.length;
          }
        }

        const calc = calcularStatusItem(quantidadeContratada, quantidadeUtilizada);

        return {
          id: itemId,
          servicos: nomeServico,
          categoria,
          quantidadeContratada,
          quantidadeUtilizada,
          saldoRestante: calc.saldoRestante,
          temSaldo: calc.temSaldo,
          percentualUtilizado: calc.percentualUtilizado,
          statusSaldo: calc.statusSaldo,
          nivelAlerta: calc.nivelAlerta,
          alertaTitulo: calc.alertaTitulo,
          alertaMensagem: calc.alertaMensagem,
          alertaBloqueado: calc.alertaBloqueado,
          valor_unitario: valorUnitario,
          valor_total: valorTotal
        };
      });

      // Separar por categorias
      const itensVeiculo = parsedItems.filter(it => it.categoria === 'veiculo');
      const itensAjudante = parsedItems.filter(it => it.categoria === 'ajudante');
      const itensOutros = parsedItems.filter(it => it.categoria !== 'veiculo' && it.categoria !== 'ajudante');

      // Totais agregados
      const totalContratado = parsedItems.reduce((acc, it) => acc + (it.quantidadeContratada > 0 ? it.quantidadeContratada : 0), 0);
      const totalUtilizado = oss.length;
      const saldoRestante = totalContratado - totalUtilizado;
      const temSaldo = parsedItems.length === 0 || parsedItems.some(it => it.temSaldo);

      let percentualUtilizado = 0;
      if (totalContratado > 0) {
        percentualUtilizado = Math.min(100, Math.round((totalUtilizado / totalContratado) * 100));
      }

      const avaliacaoGeral = avaliarNivelAlertaSaldo(totalContratado, saldoRestante);

      const clienteObj: any = pedido.pessoa;
      const clienteNome = (Array.isArray(clienteObj) ? clienteObj[0]?.nome : clienteObj?.nome) || '';

      return {
        pedidoId,
        clienteId: pedido.pessoa_id,
        clienteNome: fixEncodingIssues(clienteNome),
        totalContratado,
        totalUtilizado,
        saldoRestante,
        temSaldo,
        percentualUtilizado,
        statusSaldo: avaliacaoGeral.statusLegado,
        nivelAlertaGeral: avaliacaoGeral.nivel,
        alertaTituloGeral: avaliacaoGeral.titulo,
        alertaMensagemGeral: avaliacaoGeral.mensagem,
        alertaBloqueadoGeral: avaliacaoGeral.bloqueado,
        itens: parsedItems,
        itensVeiculo,
        itensAjudante,
        itensOutros
      };
    } catch (err) {
      console.error(`[propostaSaldoService] Erro ao calcular saldo do pedido #${pedidoId}:`, err);
      return {
        pedidoId,
        totalContratado: 0,
        totalUtilizado: 0,
        saldoRestante: 0,
        temSaldo: true,
        percentualUtilizado: 0,
        statusSaldo: 'sem_cota',
        nivelAlertaGeral: 'sem_cota',
        alertaTituloGeral: 'Sem Cota Fixa',
        alertaMensagemGeral: 'Sem controle de saldo.',
        alertaBloqueadoGeral: false,
        itens: [],
        itensVeiculo: [],
        itensAjudante: [],
        itensOutros: []
      };
    }
  },

  /**
   * Obtém o saldo de um item específico por ID dentro do objeto PropostaSaldoInfo
   */
  getItemSaldo(saldoInfo: PropostaSaldoInfo | null | undefined, itemId: string | number): PropostaItemSaldo | undefined {
    if (!saldoInfo || !saldoInfo.itens || !itemId) return undefined;
    const strId = String(itemId);
    return saldoInfo.itens.find(it => it.id === strId);
  },

  /**
   * Valida se a emissão da OS é permitida com base nos itens selecionados no formulário
   */
  validarSaldoEmissaoOs(
    saldoInfo: PropostaSaldoInfo | null | undefined,
    itemServicoId?: string | number,
    itemAjudanteId?: string | number,
    qtdAjudante: number = 1
  ): {
    permitido: boolean;
    motivoBloqueio?: string;
    itemServicoBloqueado?: PropostaItemSaldo;
    itemAjudanteBloqueado?: PropostaItemSaldo;
  } {
    if (!saldoInfo || saldoInfo.itens.length === 0) {
      return { permitido: true };
    }

    // 0. Se a proposta possui cota total contratada e o saldo restante está zerado (bloqueio nível esgotado_0)
    if (saldoInfo.totalContratado > 0 && saldoInfo.saldoRestante <= 0) {
      return {
        permitido: false,
        motivoBloqueio: `A proposta comercial #${saldoInfo.pedidoId} consumiu a totalidade de ${saldoInfo.totalContratado} cotas contratadas (${saldoInfo.totalUtilizado} já emitidas, Saldo: 0). Conforme a política do sistema, a emissão está bloqueada até aditivo ou renovação comercial.`
      };
    }

    // 1. Validação do item de serviço selecionado
    if (itemServicoId) {
      const itemServ = this.getItemSaldo(saldoInfo, itemServicoId);
      if (itemServ && itemServ.quantidadeContratada > 0 && itemServ.saldoRestante <= 0) {
        return {
          permitido: false,
          motivoBloqueio: `O item de serviço "${itemServ.servicos}" atingiu o limite contratado de ${itemServ.quantidadeContratada} ${itemServ.quantidadeContratada === 1 ? 'unidade' : 'unidades'} (${itemServ.quantidadeUtilizada} já utilizadas, Saldo: ${itemServ.saldoRestante}).`,
          itemServicoBloqueado: itemServ
        };
      }
    }

    // 2. Validação do item de ajudante selecionado
    if (itemAjudanteId) {
      const itemAjud = this.getItemSaldo(saldoInfo, itemAjudanteId);
      const qtdPedida = Math.max(1, Number(qtdAjudante) || 1);
      if (itemAjud && itemAjud.quantidadeContratada > 0 && itemAjud.saldoRestante < qtdPedida) {
        return {
          permitido: false,
          motivoBloqueio: `O serviço de ajudante "${itemAjud.servicos}" possui saldo insuficiente (Saldo disponível: ${itemAjud.saldoRestante}, Solicitado: ${qtdPedida}).`,
          itemAjudanteBloqueado: itemAjud
        };
      }
    }

    return { permitido: true };
  },

  /**
   * Carrega saldos em lote para múltiplas propostas de uma vez com alto desempenho
   */
  async getBatchSaldos(propostas: any[]): Promise<Map<number, PropostaSaldoInfo>> {
    const resultMap = new Map<number, PropostaSaldoInfo>();
    if (!propostas || propostas.length === 0) return resultMap;

    const pedidoIds = propostas.map(p => p.id).filter(Boolean);
    if (pedidoIds.length === 0) return resultMap;

    try {
      // 1. Buscar todas as OSs emitidas para as propostas
      const { data: osList } = await supabase
        .from('ordem_de_servico')
        // `servico` não existe em ordem_de_servico e derrubava a consulta (42703).
        .select('id, pedido_id, item_servico, item_ajudante, quantidade_ajudante')
        .in('pedido_id', pedidoIds)
        .neq('status_id', 3);

      // Agrupa OSs por pedido_id
      const osByPedido = new Map<number, any[]>();
      (osList || []).forEach(os => {
        if (os.pedido_id) {
          const list = osByPedido.get(os.pedido_id) || [];
          list.push(os);
          osByPedido.set(os.pedido_id, list);
        }
      });

      // 2. Processar cada proposta
      propostas.forEach(p => {
        const rawDetails = p.detalhes_do_pedido || [];
        const { items: cleanItems } = propostaMetaService.separateItemsAndMeta(rawDetails);
        const oss = osByPedido.get(p.id) || [];

        const itemServicoUsage = new Map<string, number>();
        const itemAjudanteUsage = new Map<string, number>();

        oss.forEach((os: any) => {
          if (os.item_servico) {
            const key = String(os.item_servico);
            itemServicoUsage.set(key, (itemServicoUsage.get(key) || 0) + 1);
          }
          if (os.item_ajudante) {
            const key = String(os.item_ajudante);
            const qtd = os.quantidade_ajudante && Number(os.quantidade_ajudante) > 0 ? Number(os.quantidade_ajudante) : 1;
            itemAjudanteUsage.set(key, (itemAjudanteUsage.get(key) || 0) + qtd);
          }
        });

          const parsedItems: PropostaItemSaldo[] = cleanItems.map((item: any) => {
          const itemId = String(item.id || '');
          const nomeServico = fixEncodingIssues(item.servicos || '');
          const categoria = categorizarItemProposta(nomeServico);
          const quantidadeContratada = Number(item.quantidade) || 0;
          const valorUnitario = Number(item.valor_unitario) || 0;
          const valorTotal = Number(item.valor_total) || (quantidadeContratada * valorUnitario);

          let quantidadeUtilizada = 0;
          if (categoria === 'ajudante') {
            quantidadeUtilizada = itemAjudanteUsage.get(itemId) || 0;
          } else {
            quantidadeUtilizada = itemServicoUsage.get(itemId) || 0;
          }

          if (quantidadeUtilizada === 0 && nomeServico) {
            const matchesByText = oss.filter(
              (os: any) => !os.item_servico && os.servico && os.servico.trim().toLowerCase() === nomeServico.trim().toLowerCase()
            );
            if (matchesByText.length > 0) {
              quantidadeUtilizada += matchesByText.length;
            }
          }

          const calc = calcularStatusItem(quantidadeContratada, quantidadeUtilizada);

          return {
            id: itemId,
            servicos: nomeServico,
            categoria,
            quantidadeContratada,
            quantidadeUtilizada,
            saldoRestante: calc.saldoRestante,
            temSaldo: calc.temSaldo,
            percentualUtilizado: calc.percentualUtilizado,
            statusSaldo: calc.statusSaldo,
            nivelAlerta: calc.nivelAlerta,
            alertaTitulo: calc.alertaTitulo,
            alertaMensagem: calc.alertaMensagem,
            alertaBloqueado: calc.alertaBloqueado,
            valor_unitario: valorUnitario,
            valor_total: valorTotal
          };
        });

        const itensVeiculo = parsedItems.filter(it => it.categoria === 'veiculo');
        const itensAjudante = parsedItems.filter(it => it.categoria === 'ajudante');
        const itensOutros = parsedItems.filter(it => it.categoria !== 'veiculo' && it.categoria !== 'ajudante');

        const totalContratado = parsedItems.reduce((acc, it) => acc + (it.quantidadeContratada > 0 ? it.quantidadeContratada : 0), 0);
        const totalUtilizado = oss.length;
        const saldoRestante = totalContratado - totalUtilizado;
        const temSaldo = parsedItems.length === 0 || parsedItems.some(it => it.temSaldo);

        let percentualUtilizado = 0;
        if (totalContratado > 0) {
          percentualUtilizado = Math.min(100, Math.round((totalUtilizado / totalContratado) * 100));
        }

        const avaliacaoGeral = avaliarNivelAlertaSaldo(totalContratado, saldoRestante);

        const clienteObj: any = p.pessoa;
        const clienteNome = (Array.isArray(clienteObj) ? clienteObj[0]?.nome : clienteObj?.nome) || '';

        resultMap.set(p.id, {
          pedidoId: p.id,
          clienteId: p.pessoa_id,
          clienteNome: fixEncodingIssues(clienteNome),
          totalContratado,
          totalUtilizado,
          saldoRestante,
          temSaldo,
          percentualUtilizado,
          statusSaldo: avaliacaoGeral.statusLegado,
          nivelAlertaGeral: avaliacaoGeral.nivel,
          alertaTituloGeral: avaliacaoGeral.titulo,
          alertaMensagemGeral: avaliacaoGeral.mensagem,
          alertaBloqueadoGeral: avaliacaoGeral.bloqueado,
          itens: parsedItems,
          itensVeiculo,
          itensAjudante,
          itensOutros
        });
      });
    } catch (err) {
      console.error('[propostaSaldoService] Erro no carregamento em lote de saldos:', err);
    }

    return resultMap;
  }
};
